/**
 * Assignment Billing Configuration Component
 *
 * Manages assignment-level billing configuration overrides.
 * Shows inherited project defaults and allows creating assignment-specific overrides.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format, parseISO, isBefore } from 'date-fns';
import billingConfigService from '../../services/billingConfigService';
import ConfirmDialog from '../Common/ConfirmDialog';

const AssignmentBillingConfig = ({ assignmentId, projectCode, assignmentBillRate }) => {
  const [discountConfigs, setDiscountConfigs] = useState([]);
  const [minDaysConfigs, setMinDaysConfigs] = useState([]);
  const [maxDaysConfigs, setMaxDaysConfigs] = useState([]);
  const [billRateConfigs, setBillRateConfigs] = useState([]);
  const [projectDefaults, setProjectDefaults] = useState({
    discount: [],
    minDays: [],
    maxDays: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'discount', 'min_days', 'max_days', 'bill_rate'
  const [editingConfig, setEditingConfig] = useState(null);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [deleteConfigType, setDeleteConfigType] = useState('');

  const [formData, setFormData] = useState({
    effective_from: '',
    end_date: '',
    value: '',
  });

  useEffect(() => {
    if (assignmentId && projectCode) {
      loadAllConfigs();
    }
  }, [assignmentId, projectCode]);

  const loadAllConfigs = async () => {
    try {
      setLoading(true);
      // Load assignment overrides
      const [assignmentDiscount, assignmentMinDays, assignmentMaxDays] = await Promise.all([
        billingConfigService.getAssignmentDiscountConfigs(assignmentId),
        billingConfigService.getAssignmentMinDaysConfigs(assignmentId),
        billingConfigService.getAssignmentMaxDaysConfigs(assignmentId),
      ]);
      setDiscountConfigs(assignmentDiscount);
      setMinDaysConfigs(assignmentMinDays);
      setMaxDaysConfigs(assignmentMaxDays);

      // Load bill rate configs separately (newer feature, handle gracefully)
      try {
        const assignmentBillRates = await billingConfigService.getAssignmentBillRateConfigs(assignmentId);
        setBillRateConfigs(assignmentBillRates);
      } catch (billRateErr) {
        console.warn('Failed to load bill rate configs:', billRateErr);
        setBillRateConfigs([]);
      }

      // Load project defaults
      const [projectDiscount, projectMinDays, projectMaxDays] = await Promise.all([
        billingConfigService.getProjectDiscountConfigs(projectCode),
        billingConfigService.getProjectMinDaysConfigs(projectCode),
        billingConfigService.getProjectMaxDaysConfigs(projectCode),
      ]);
      setProjectDefaults({
        discount: projectDiscount,
        minDays: projectMinDays,
        maxDays: projectMaxDays,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type, config = null) => {
    setDialogType(type);
    setEditingConfig(config);
    if (config) {
      setFormData({
        effective_from: config.effective_from,
        end_date: config.end_date || '',
        value: getValueFromConfig(config, type),
      });
    } else {
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      nextMonth.setDate(1);
      const defaultValue = type === 'discount' ? '0' :
                           type === 'min_days' ? '0' :
                           type === 'max_days' ? '31' :
                           assignmentBillRate?.toString() || '0';
      setFormData({
        effective_from: format(nextMonth, 'yyyy-MM-01'),
        end_date: '',
        value: defaultValue,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogType('');
    setEditingConfig(null);
    setFormData({ effective_from: '', end_date: '', value: '' });
    setError('');
  };

  const getValueFromConfig = (config, type) => {
    if (type === 'discount') return config.discount_percentage;
    if (type === 'min_days') return config.min_billable_days;
    if (type === 'max_days') return config.max_billable_days;
    return config.bill_rate;
  };

  const validateForm = () => {
    const value = parseFloat(formData.value);

    if (!formData.effective_from) {
      setError('Effective date is required');
      return false;
    }

    const date = parseISO(formData.effective_from);
    if (date.getDate() !== 1) {
      setError('Effective date must be the first day of a month');
      return false;
    }

    // Validate end_date if provided
    if (formData.end_date) {
      const endDate = parseISO(formData.end_date);
      const lastDayOfMonth = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();

      if (endDate.getDate() !== lastDayOfMonth) {
        setError('End date must be the last day of a month');
        return false;
      }
      if (endDate < date) {
        setError('End date must be on or after effective date');
        return false;
      }
    }

    if (formData.value === '' || isNaN(value)) {
      setError('Value is required and must be a number');
      return false;
    }

    if (dialogType === 'discount' && (value < 0 || value > 100)) {
      setError('Discount must be between 0 and 100');
      return false;
    }

    if (dialogType === 'min_days' && value < 0) {
      setError('Min billable days must be >= 0');
      return false;
    }

    if (dialogType === 'max_days' && value <= 0) {
      setError('Max billable days must be > 0');
      return false;
    }

    if (dialogType === 'bill_rate' && value <= 0) {
      setError('Bill rate must be > 0');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setError('');
      const value = parseFloat(formData.value);
      const fieldName = dialogType === 'discount' ? 'discount_percentage' :
                         dialogType === 'min_days' ? 'min_billable_days' :
                         dialogType === 'max_days' ? 'max_billable_days' : 'bill_rate';
      const data = {
        effective_from: formData.effective_from,
        end_date: formData.end_date || null,
        [fieldName]: value,
      };

      if (editingConfig) {
        if (dialogType === 'discount') {
          await billingConfigService.updateAssignmentDiscountConfig(assignmentId, editingConfig.id, data);
        } else if (dialogType === 'min_days') {
          await billingConfigService.updateAssignmentMinDaysConfig(assignmentId, editingConfig.id, data);
        } else if (dialogType === 'max_days') {
          await billingConfigService.updateAssignmentMaxDaysConfig(assignmentId, editingConfig.id, data);
        } else {
          await billingConfigService.updateAssignmentBillRateConfig(assignmentId, editingConfig.id, data);
        }
        setSuccess('Override updated successfully');
      } else {
        if (dialogType === 'discount') {
          await billingConfigService.createAssignmentDiscountConfig(assignmentId, data);
        } else if (dialogType === 'min_days') {
          await billingConfigService.createAssignmentMinDaysConfig(assignmentId, data);
        } else if (dialogType === 'max_days') {
          await billingConfigService.createAssignmentMaxDaysConfig(assignmentId, data);
        } else {
          await billingConfigService.createAssignmentBillRateConfig(assignmentId, data);
        }
        setSuccess('Override created successfully');
      }

      await loadAllConfigs();
      handleCloseDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save override');
    }
  };

  const handleDeleteClick = (type, config) => {
    setConfigToDelete(config);
    setDeleteConfigType(type);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return;
    try {
      if (deleteConfigType === 'discount') {
        await billingConfigService.deleteAssignmentDiscountConfig(assignmentId, configToDelete.id);
      } else if (deleteConfigType === 'min_days') {
        await billingConfigService.deleteAssignmentMinDaysConfig(assignmentId, configToDelete.id);
      } else if (deleteConfigType === 'max_days') {
        await billingConfigService.deleteAssignmentMaxDaysConfig(assignmentId, configToDelete.id);
      } else {
        await billingConfigService.deleteAssignmentBillRateConfig(assignmentId, configToDelete.id);
      }

      setSuccess('Override deleted successfully');
      await loadAllConfigs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete override');
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
      setDeleteConfigType('');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setConfigToDelete(null);
    setDeleteConfigType('');
  };

  const getCurrentProjectValue = (type) => {
    const today = new Date();
    const configs = type === 'discount' ? projectDefaults.discount :
                    type === 'min_days' ? projectDefaults.minDays :
                    projectDefaults.maxDays;

    // Find most recent config before today
    const activeConfig = configs
      .filter(c => isBefore(parseISO(c.effective_from), today) ||
                   format(parseISO(c.effective_from), 'yyyy-MM-01') === format(today, 'yyyy-MM-01'))
      .sort((a, b) => parseISO(b.effective_from) - parseISO(a.effective_from))[0];

    if (!activeConfig) {
      return type === 'discount' ? '0%' : type === 'min_days' ? '0 days' : '31 days';
    }

    if (type === 'discount') return `${activeConfig.discount_percentage}%`;
    if (type === 'min_days') return `${activeConfig.min_billable_days} days`;
    return `${activeConfig.max_billable_days} days`;
  };

  const getCurrentEffectiveBillRate = () => {
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');

    // Find active override (effective_from <= today AND (end_date is null OR end_date >= today))
    const activeOverride = billRateConfigs
      .filter(c => {
        const effectiveFrom = c.effective_from;
        const endDate = c.end_date;
        return effectiveFrom <= todayStr && (!endDate || endDate >= todayStr);
      })
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];

    if (activeOverride) {
      return { rate: activeOverride.bill_rate, source: 'override', config: activeOverride };
    }
    return { rate: assignmentBillRate || 0, source: 'default', config: null };
  };

  const renderBillRateSection = () => {
    const effectiveRate = getCurrentEffectiveBillRate();
    const isOverridden = effectiveRate.source === 'override';

    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Bill Rate</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('bill_rate')}
          >
            Add Override
          </Button>
        </Box>

        <Alert severity={isOverridden ? 'warning' : 'info'} sx={{ mb: 1 }}>
          <strong>Current Effective Rate:</strong> ${effectiveRate.rate?.toFixed(2) || '0.00'}/hour
          {isOverridden && (
            <Chip
              label="Overridden"
              color="warning"
              size="small"
              sx={{ ml: 1 }}
            />
          )}
          {isOverridden && effectiveRate.config?.end_date && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Override expires: {format(parseISO(effectiveRate.config.end_date), 'MMM yyyy')}
            </Typography>
          )}
        </Alert>

        {isOverridden && (
          <Alert severity="info" sx={{ mb: 1 }}>
            <strong>Assignment Default:</strong> ${assignmentBillRate?.toFixed(2) || '0.00'}/hour
            <Typography variant="caption" display="block">
              (Will be used when no override is active)
            </Typography>
          </Alert>
        )}

        {billRateConfigs.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
            No overrides - using assignment default
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Effective From</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Bill Rate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billRateConfigs.map((config) => {
                  const isActive = isBefore(parseISO(config.effective_from), new Date());
                  return (
                    <TableRow key={config.id}>
                      <TableCell>{format(parseISO(config.effective_from), 'MMM yyyy')}</TableCell>
                      <TableCell>
                        {config.end_date ? format(parseISO(config.end_date), 'MMM yyyy') : 'Ongoing'}
                      </TableCell>
                      <TableCell>${config.bill_rate.toFixed(2)}/hour</TableCell>
                      <TableCell>
                        {isActive ? (
                          <Chip label="Active" color="success" size="small" />
                        ) : (
                          <Chip label="Future" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => handleOpenDialog('bill_rate', config)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick('bill_rate', config)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  const renderOverrideSection = (title, type, configs) => {
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">{title}</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(type)}
          >
            Add Override
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 1 }}>
          <strong>Project Default:</strong> {getCurrentProjectValue(type)}
        </Alert>

        {configs.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
            No overrides - using project defaults
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Effective From</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Override Value</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map((config) => {
                  const isActive = isBefore(parseISO(config.effective_from), new Date());
                  return (
                    <TableRow key={config.id}>
                      <TableCell>{format(parseISO(config.effective_from), 'MMM yyyy')}</TableCell>
                      <TableCell>
                        {config.end_date ? format(parseISO(config.end_date), 'MMM yyyy') : 'Ongoing'}
                      </TableCell>
                      <TableCell>
                        {type === 'discount' ? `${config.discount_percentage}%` :
                         type === 'min_days' ? `${config.min_billable_days} days` :
                         `${config.max_billable_days} days`}
                      </TableCell>
                      <TableCell>
                        {isActive ? (
                          <Chip label="Active" color="success" size="small" />
                        ) : (
                          <Chip label="Future" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => handleOpenDialog(type, config)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(type, config)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  if (loading) return <Typography>Loading billing configurations...</Typography>;

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Configure assignment-specific overrides. If no override exists, the project defaults will be used.
      </Typography>

      {renderBillRateSection()}
      <Divider sx={{ my: 3 }} />
      {renderOverrideSection('Discount Percentage', 'discount', discountConfigs)}
      <Divider sx={{ my: 3 }} />
      {renderOverrideSection('Minimum Billable Days', 'min_days', minDaysConfigs)}
      <Divider sx={{ my: 3 }} />
      {renderOverrideSection('Maximum Billable Days', 'max_days', maxDaysConfigs)}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingConfig ? 'Edit Override' : 'Add Override'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Effective From"
            type="month"
            fullWidth
            margin="normal"
            value={formData.effective_from ? formData.effective_from.substring(0, 7) : ''}
            onChange={(e) => setFormData({ ...formData, effective_from: e.target.value + '-01' })}
            helperText="Override becomes active from the first day of this month"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="End Date (Optional)"
            type="month"
            fullWidth
            margin="normal"
            value={formData.end_date ? formData.end_date.substring(0, 7) : ''}
            onChange={(e) => {
              if (e.target.value) {
                // Calculate last day of selected month
                const [year, month] = e.target.value.split('-');
                const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
                setFormData({ ...formData, end_date: `${e.target.value}-${lastDay.toString().padStart(2, '0')}` });
              } else {
                setFormData({ ...formData, end_date: '' });
              }
            }}
            helperText="Override is active through the end of this month."
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label={
              dialogType === 'discount' ? 'Discount Percentage' :
              dialogType === 'min_days' ? 'Min Billable Days' :
              dialogType === 'max_days' ? 'Max Billable Days' :
              'Bill Rate ($/hour)'
            }
            type="number"
            fullWidth
            margin="normal"
            value={formData.value}
            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
            helperText={
              dialogType === 'discount' ? `Project default: ${getCurrentProjectValue('discount')}` :
              dialogType === 'min_days' ? `Project default: ${getCurrentProjectValue('min_days')}` :
              dialogType === 'max_days' ? `Project default: ${getCurrentProjectValue('max_days')}` :
              `Current effective: $${getCurrentEffectiveBillRate().rate?.toFixed(2) || '0.00'}/hour (Default: $${assignmentBillRate?.toFixed(2) || '0.00'})`
            }
            inputProps={{
              min: dialogType === 'max_days' || dialogType === 'bill_rate' ? 0.01 : 0,
              max: dialogType === 'discount' ? 100 : undefined,
              step: dialogType === 'discount' || dialogType === 'bill_rate' ? 0.01 : 1,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editingConfig ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Configuration"
        message="Are you sure you want to delete this billing configuration override?"
        details={configToDelete ? [
          { label: 'Type', value: deleteConfigType === 'bill_rate' ? 'Bill Rate' : deleteConfigType === 'discount' ? 'Discount' : deleteConfigType === 'min_days' ? 'Min Days' : 'Max Days' },
          { label: 'Effective From', value: format(parseISO(configToDelete.effective_from), 'MMM dd, yyyy') },
        ] : []}
      />
    </Box>
  );
};

export default AssignmentBillingConfig;
